import React, { useMemo } from "react";
import useStore from "../../../context/useStore";
import Loader from "../../common/Loader";
import SaveBtn from "../../common/SaveBtn";
import { estAnonymise } from "../../../helpers/allocataire";
import { modalActions } from "../../../context/actions/modalActions";
import { FicheSection, StickySaveBar, TabPanel, Tabs, useFicheTabs } from "../../common/Fiche";
import Simulator from "../Simulator/Simulator";
import Reorientations from "../Reorientations/Reorientations";
import CERModule from "./CERModule/CERModule";
import FormRdv from "./FormRdv";
import PageUtilisateurAnonymise from "./PageUtilisateurAnonymise";
import RecipientActions from "./RecipientActions";
import RecipientHeader from "./RecipientHeader";
import RecipientInfo from "./RecipientInfo";
import RecipientRdoHistory from "./RecipientRdoHistory";
import RecipientRdv from "./RecipientRdv/RecipientRdv";
import RecipientTimeline from "./RecipientTimeline";
import useRecipientDetail from "./useRecipientDetail";
import { buildRecipientTabs } from "./recipientFacts";

export const TABS_PREFIX = "fiche-allocataire";
export const TAB_STORAGE_KEY = "espoar.boa.recipientDetail.tab";

// Fiche allocataire : en-tête d'identité (faits, pastilles, actions) + onglets
// Informations · Contrat CER · RDO · Rendez-vous · Parcours. Les données et
// actions vivent dans useRecipientDetail ; en mode intégré (prop recipientId,
// ouverture en modale) : pas de retour, pas d'enregistrement, pas de hash.
const RecipientDetail = (props) => {
  const [state, dispatch] = useStore();
  const isModal = !!props.recipientId;
  const idToGet = props.recipientId || props.match?.params?.id;
  const user = state.auth?.user;
  const constants = Array.isArray(state.constants.items) ? {} : state.constants.items;
  const modal_actions = modalActions(state, dispatch);
  const detail = useRecipientDetail({ idToGet, user });
  const { recipient, rdsps, rdvs, events, formConfig, errors } = detail;

  const tabs = useMemo(
    () => buildRecipientTabs({ recipient, rdsps, formConfig, events, rdvs, role: user?.role }),
    [recipient, rdsps, formConfig, events, rdvs, user]
  );
  const { activeId, select } = useFicheTabs({
    tabs,
    defaultId: "parcours",
    storageKey: isModal ? null : TAB_STORAGE_KEY,
    syncHash: !isModal,
  });

  const addRdsp = (rdspDatas = false) =>
    modal_actions.updateModal({
      isOpen: true,
      content: <Simulator recipient={recipient} rdsp={rdspDatas} successCallback={detail.getDatas} />,
    });

  const openReorientations = () =>
    modal_actions.updateModal({
      isOpen: true,
      content: <Reorientations userRecipientId={idToGet} isModal={true} />,
    });

  if (state.constants.items.length === 0 || detail.isLoading) return <Loader />;
  if (estAnonymise(recipient)) return <PageUtilisateurAnonymise />;

  const isCaf = !recipient.lastOrientation || recipient.lastOrientation.orientation === "CAF";

  return (
    <div className={`fiche-page ${isModal ? "fiche-page--modal" : "container-fluid px-5 my-5"}`}>
      {errors && errors.other && (
        <div className="alert alert-danger" role="alert">
          {errors.other}
        </div>
      )}

      <RecipientHeader
        recipient={recipient}
        constants={constants}
        orgs={detail.orgs}
        back={isModal ? null : "/recipients"}
        role={user?.role}
        onAccompagnementCorrige={detail.refreshEvents}
        actions={
          <RecipientActions
            recipient={recipient}
            refresh={detail.getDatas}
            isSaving={detail.isSaving}
            isModal={isModal}
            unlockAccount={detail.unlockAccount}
            resendActivationMail={detail.resendActivationMail}
            onSynchroForced={detail.refreshSynchrosFT}
          />
        }
      />

      <Tabs tabs={tabs} activeId={activeId} onChange={select} idPrefix={TABS_PREFIX} ariaLabel="Sections de la fiche" />

      <TabPanel id="informations" activeId={activeId} idPrefix={TABS_PREFIX}>
        <RecipientInfo
          recipient={recipient}
          allDisabled={detail.allDisabled}
          genders={constants.GENDERS}
          maritalStatuses={constants.MARITAL_STATUS}
          errors={errors}
          updateData={detail.updateData}
          rdsps={rdsps}
          refresh={detail.getDatas}
          isSaving={detail.isSaving}
        />
        {!isModal && (
          <>
            <div className="fiche-panel__footer">
              <SaveBtn
                margin=""
                className="py-3 px-4 align-items-center justify-content-center"
                text="Enregistrer les informations"
                save={detail.save}
                isSaving={detail.isSaving}
                isSaved={false}
              />
            </div>
            <StickySaveBar
              visible={detail.infoUpdated}
              isSaving={detail.isSaving}
              onSave={detail.save}
              message="Informations modifiées, non enregistrées"
              label="Enregistrer les informations"
            />
          </>
        )}
      </TabPanel>

      <TabPanel id="cer" activeId={activeId} idPrefix={TABS_PREFIX}>
        <FicheSection
          id="fiche-cer"
          title={
            <>
              Contrat CER
              {!isCaf && (
                <span className="fiche-tabs__hint">
                  <i className="fa fa-exclamation-triangle mr-1" aria-hidden="true" />
                  allocataire non CAF
                </span>
              )}
            </>
          }
        >
          <CERModule recipient={recipient} />
        </FicheSection>
      </TabPanel>

      <TabPanel id="rdo" activeId={activeId} idPrefix={TABS_PREFIX}>
        <FicheSection id="fiche-rdo" title="Historique des RDO">
          {rdsps && formConfig ? (
            <RecipientRdoHistory
              rdsps={rdsps}
              recipient={recipient}
              onAddRdsp={addRdsp}
              onSynchroForced={() => {
                detail.refreshSynchrosFT();
                detail.refreshRdsps();
              }}
            />
          ) : (
            <Loader />
          )}
        </FicheSection>
      </TabPanel>

      <TabPanel id="rdv" activeId={activeId} idPrefix={TABS_PREFIX}>
        <FicheSection id="fiche-rdv" title="Rendez-vous">
          {detail.isLoadingRdvs ? (
            <Loader />
          ) : (
            <>
              {!detail.hasRdv && detail.rdspId && (
                <div className="my-3">
                  <p className="rdv-intro">
                    Aucun rendez-vous positionné pour le moment — créez-en un à l'aide du
                    formulaire ci-dessous.
                  </p>
                  <FormRdv rdspId={detail.rdspId} userData={{ id: idToGet }} />
                </div>
              )}
              {rdvs ? (
                <RecipientRdv
                  rdvs={rdvs}
                  errors={errors}
                  setErrors={detail.setErrors}
                  recipientId={idToGet}
                  getRdvs={detail.getDatas}
                  isLoading={detail.isLoadingRdvs}
                  rdspId={detail.rdspId}
                  recipient={recipient}
                />
              ) : (
                <Loader />
              )}
            </>
          )}
        </FicheSection>
      </TabPanel>

      <TabPanel id="parcours" activeId={activeId} idPrefix={TABS_PREFIX}>
        <FicheSection
          id="fiche-parcours"
          title="Parcours"
          actions={
            <button type="button" className="btn btn-outline-dark btn-sm" onClick={openReorientations}>
              <i className="fa fa-map-signs mr-2" aria-hidden="true" />
              Historique des orientations
            </button>
          }
        >
          {detail.mergedEvents ? <RecipientTimeline events={detail.mergedEvents} /> : <Loader />}
        </FicheSection>
      </TabPanel>
    </div>
  );
};

export default RecipientDetail;
